import { useCallback, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import {
  ChevronsLeft,
  ChevronsRight,
  Eye,
  EyeOff,
  FolderLock,
  GripVertical,
  Lock,
  LockOpen,
} from "lucide-react";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { chromeSelectClass } from "@/lib/map-chrome";
import { cn } from "@/lib/utils";
import type {
  VisualizerLayerGroup,
  VisualizerLibrary,
  VisualizerScene,
  VisualizerSignFeature,
} from "@/services/visualizer.service";

export type LibraryPick =
  | { source: "kls"; klsId: number; name: string }
  | { source: "asset"; assetId: number; name: string };

export const VISUALIZER_LAYERS_WIDTH_MIN = 240;
export const VISUALIZER_LAYERS_WIDTH_DEFAULT = 300;

/** Максимум — половина окна, чтобы карта оставалась видимой. */
export function maxVisualizerLayersWidthPx(): number {
  if (typeof window === "undefined") return 800;
  return Math.max(VISUALIZER_LAYERS_WIDTH_MIN, Math.floor(window.innerWidth * 0.5));
}

export function clampVisualizerLayersWidth(px: number): number {
  return Math.min(maxVisualizerLayersWidthPx(), Math.max(VISUALIZER_LAYERS_WIDTH_MIN, Math.round(px)));
}

type VisualizerLayersPanelProps = {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  widthPx: number;
  onWidthChange: (widthPx: number) => void;
  canManage: boolean;
  scenes: VisualizerScene[];
  sceneId: number | null;
  sceneName: string;
  onSceneNameChange: (value: string) => void;
  onSelectScene: (id: number) => void;
  onCreateScene: () => void;
  onDeleteScene: () => void;
  onExportScene?: () => void;
  onImportScene?: (file: File) => void;
  onDuplicateScene?: () => void;
  exportBusy?: boolean;
  importBusy?: boolean;
  duplicateBusy?: boolean;
  signs: VisualizerSignFeature[];
  layerGroups: VisualizerLayerGroup[];
  selectedIds: number[];
  onSelectSign: (id: number, opts?: { shiftKey?: boolean }) => void;
  onDeleteSign: (id: number) => void;
  onCreateLayerGroup: (name: string) => void;
  onRenameLayerGroup: (groupId: number, name: string) => void;
  onDeleteLayerGroup: (groupId: number) => void;
  onToggleGroupLock: (groupId: number, locked: boolean) => void;
  onToggleGroupVisible: (groupId: number, visible: boolean) => void;
  onToggleSignLock: (signId: number, locked: boolean) => void;
  onToggleSignVisible: (signId: number, visible: boolean) => void;
  onAssignSignGroup: (signId: number, groupId: number | null) => void;
  library: VisualizerLibrary | undefined;
  pick: LibraryPick | null;
  onPick: (pick: LibraryPick) => void;
  onUpload: (file: File) => void;
  styleForm: ReactNode;
  signCardForm?: ReactNode;
};

function signTitle(feature: VisualizerSignFeature): string {
  const name = feature.properties?.name || feature.properties?.label || `Знак ${feature.properties?.id}`;
  const kind =
    feature.properties?.kind === "text"
      ? "текст"
      : feature.geometry.type === "LineString"
        ? "линия"
        : feature.geometry.type === "Polygon"
          ? "площадь"
          : "точка";
  return `${name} · ${kind}`;
}

function SignRows({
  features,
  selectedIds,
  canManage,
  layerGroups,
  onSelectSign,
  onDeleteSign,
  onToggleSignLock,
  onToggleSignVisible,
  onAssignSignGroup,
}: {
  features: VisualizerSignFeature[];
  selectedIds: number[];
  canManage: boolean;
  layerGroups: VisualizerLayerGroup[];
  onSelectSign: (id: number, opts?: { shiftKey?: boolean }) => void;
  onDeleteSign: (id: number) => void;
  onToggleSignLock: (signId: number, locked: boolean) => void;
  onToggleSignVisible: (signId: number, visible: boolean) => void;
  onAssignSignGroup: (signId: number, groupId: number | null) => void;
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  return (
    <ul className="space-y-0.5">
      {features.map((feature) => {
        const id = Number(feature.properties?.id);
        const locked = Boolean(feature.properties?.effectivelyLocked);
        const ownLocked = Boolean(feature.properties?.isLocked);
        const ownVisible = feature.properties?.isVisible !== false;
        const effectivelyVisible = feature.properties?.effectivelyVisible !== false;
        const groupId = feature.properties?.layerGroupId ?? null;
        const isSelected = selectedSet.has(id);
        return (
          <li key={id} className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              className={cn(
                "flex min-w-0 flex-1 items-center gap-1 truncate rounded px-2 py-1 text-left hover:bg-muted",
                isSelected && "bg-muted font-medium",
                !effectivelyVisible && "opacity-60",
              )}
              aria-pressed={isSelected}
              onClick={(event) => onSelectSign(id, { shiftKey: event.shiftKey })}
            >
              {locked ? <Lock className="h-3 w-3 shrink-0 text-amber-700" aria-hidden /> : null}
              {!effectivelyVisible ? (
                <EyeOff className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
              ) : null}
              <span className="truncate">{signTitle(feature)}</span>
            </button>
            {canManage && (
              <>
                <select
                  className={cn(chromeSelectClass, "h-7 max-w-[7rem] text-[11px]")}
                  value={groupId != null ? String(groupId) : ""}
                  title="Группа знака"
                  aria-label={`Группа для ${signTitle(feature)}`}
                  disabled={locked}
                  onChange={(event) => {
                    const value = event.target.value;
                    onAssignSignGroup(id, value ? Number(value) : null);
                  }}
                >
                  <option value="">Без группы</option>
                  {layerGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  title={ownVisible ? "Скрыть знак" : "Показать знак"}
                  aria-label={ownVisible ? "Скрыть знак" : "Показать знак"}
                  onClick={() => onToggleSignVisible(id, !ownVisible)}
                >
                  {ownVisible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  title={ownLocked ? "Снять блокировку знака" : "Заблокировать знак"}
                  aria-label={ownLocked ? "Снять блокировку знака" : "Заблокировать знак"}
                  onClick={() => onToggleSignLock(id, !ownLocked)}
                >
                  {ownLocked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  title={locked ? "Знак заблокирован" : "Удалить знак"}
                  aria-label={`Удалить ${signTitle(feature)}`}
                  disabled={locked}
                  onClick={() => onDeleteSign(id)}
                >
                  ×
                </Button>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function VisualizerLayersPanel({
  collapsed,
  onCollapsedChange,
  widthPx,
  onWidthChange,
  canManage,
  scenes,
  sceneId,
  sceneName,
  onSceneNameChange,
  onSelectScene,
  onCreateScene,
  onDeleteScene,
  onExportScene,
  onImportScene,
  onDuplicateScene,
  exportBusy = false,
  importBusy = false,
  duplicateBusy = false,
  signs,
  layerGroups,
  selectedIds,
  onSelectSign,
  onDeleteSign,
  onCreateLayerGroup,
  onRenameLayerGroup,
  onDeleteLayerGroup,
  onToggleGroupLock,
  onToggleGroupVisible,
  onToggleSignLock,
  onToggleSignVisible,
  onAssignSignGroup,
  library,
  pick,
  onPick,
  onUpload,
  styleForm,
  signCardForm,
}: VisualizerLayersPanelProps) {
  const [resizing, setResizing] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const importInputRef = useRef<HTMLInputElement>(null);

  const signsByGroup = useMemo(() => {
    const map = new Map<number | null, VisualizerSignFeature[]>();
    map.set(null, []);
    for (const group of layerGroups) map.set(group.id, []);
    for (const feature of signs) {
      const raw = feature.properties?.layerGroupId;
      let gid: number | null =
        raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
      if (gid != null && !map.has(gid)) gid = null;
      map.get(gid)!.push(feature);
    }
    return map;
  }, [signs, layerGroups]);

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();
      const handle = event.currentTarget;
      const startX = event.clientX;
      const startW = widthPx;
      setResizing(true);
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      const onMove = (ev: PointerEvent) => {
        onWidthChange(clampVisualizerLayersWidth(startW + (startX - ev.clientX)));
      };
      const onUp = (ev: PointerEvent) => {
        try {
          if (handle.hasPointerCapture(ev.pointerId)) handle.releasePointerCapture(ev.pointerId);
        } catch {
          /* ignore */
        }
        setResizing(false);
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.removeEventListener("pointercancel", onUp);
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.addEventListener("pointercancel", onUp);
    },
    [onWidthChange, widthPx],
  );

  if (collapsed) {
    return (
      <aside
        className={cn(
          "flex h-full w-10 shrink-0 flex-col items-center gap-2 border-l bg-background/95 py-2",
          chromeSelectClass,
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onCollapsedChange(false)}
          aria-label="Развернуть панель слоёв"
          title="Развернуть"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <div
          className="mt-1 select-none text-xs font-medium uppercase tracking-wide text-muted-foreground"
          style={{ writingMode: "vertical-rl" }}
        >
          Слои
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        "relative flex h-full shrink-0 flex-col overflow-hidden border-l bg-background/95",
        !resizing && "transition-[width] duration-150 ease-out",
        chromeSelectClass,
      )}
      style={{ width: widthPx }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Изменить ширину панели слоёв"
        title="Потяните, чтобы изменить ширину"
        className="group absolute bottom-0 left-0 top-0 z-20 flex w-3 -translate-x-1.5 cursor-col-resize touch-none select-none items-center justify-center hover:bg-muted/40"
        onPointerDown={startResize}
      >
        <span
          className={cn(
            "flex h-12 w-1 items-center justify-center rounded-full bg-border group-hover:bg-primary/50",
            resizing && "bg-primary",
          )}
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      </div>
      <div className="flex items-center gap-1 border-b px-2 py-2 pl-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide">Слои</h2>
        <SectionHelpTooltip label="Справка: панель слоёв Визуализатора">
          <p>Справа — сцены и знаки открытой сцены. Клик по знаку выделяет его на карте.</p>
          <p>
            Shift+клик по знаку добавляет или убирает его из мультивыбора (синхронно с картой и
            лассо).
          </p>
          <p>Библиотека ниже: выберите знак, затем режим на тулбаре. Esc снимает выделение.</p>
          <p>Стрелки сворачивают панель в полоску. Левый край панели тяните, чтобы изменить ширину.</p>
        </SectionHelpTooltip>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-auto h-8 w-8"
          onClick={() => onCollapsedChange(true)}
          aria-label="Свернуть панель слоёв"
          title="Свернуть"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 text-sm">
        <section className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Сцены</h3>
          <ul className="space-y-0.5">
            {scenes.map((scene) => (
              <li key={scene.id}>
                <button
                  type="button"
                  className={cn(
                    "w-full rounded px-2 py-1 text-left hover:bg-muted",
                    sceneId === scene.id && "bg-muted font-medium",
                  )}
                  onClick={() => onSelectScene(scene.id)}
                >
                  {scene.name}
                </button>
              </li>
            ))}
          </ul>
          {canManage && (
            <form
              className="flex gap-1"
              onSubmit={(event) => {
                event.preventDefault();
                onCreateScene();
              }}
            >
              <Input
                value={sceneName}
                onChange={(event) => onSceneNameChange(event.target.value)}
                placeholder="Имя сцены"
                aria-label="Имя новой сцены"
              />
              <Button type="submit" size="sm" disabled={!sceneName.trim()}>
                +
              </Button>
            </form>
          )}
          {canManage && sceneId != null && (
            <Button type="button" variant="outline" size="sm" className="w-full" onClick={onDeleteScene}>
              Удалить сцену
            </Button>
          )}
          {(onExportScene || onImportScene || onDuplicateScene) && (
            <div className="space-y-1.5 border-t pt-2">
              <div className="flex items-center gap-1">
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Перенос сцены
                </h3>
                <SectionHelpTooltip label="Справка: перенос и дублирование сцены">
                  <p>Экспорт — JSON-пакет для другого контура или резервной копии.</p>
                  <p>Импорт всегда создаёт новую сцену; привязки знаков к объектам каталога (ОИ) не переносятся.</p>
                  <p>
                    Дублировать — копия в этом же контуре: группы, знаки, карточки и привязки к ОИ
                    сохраняются. Исходная сцена не меняется.
                  </p>
                </SectionHelpTooltip>
              </div>
              <div className="flex flex-wrap gap-1">
                {canManage && onDuplicateScene && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={sceneId == null || duplicateBusy}
                    onClick={onDuplicateScene}
                  >
                    {duplicateBusy ? "…" : "Дублировать"}
                  </Button>
                )}
                {onExportScene && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={sceneId == null || exportBusy}
                    onClick={onExportScene}
                  >
                    {exportBusy ? "…" : "Экспорт"}
                  </Button>
                )}
                {canManage && onImportScene && (
                  <>
                    <input
                      ref={importInputRef}
                      type="file"
                      accept="application/json,.json"
                      className="hidden"
                      aria-hidden
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file) onImportScene(file);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      disabled={importBusy}
                      onClick={() => importInputRef.current?.click()}
                    >
                      {importBusy ? "…" : "Импорт"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-1">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Знаки сцены
            </h3>
            <SectionHelpTooltip label="Справка: группы и блокировка">
              <p>Группы — папки одного уровня для организации знаков сцены.</p>
              <p>
                Глаз скрывает знак или всю группу на карте и в легенде (список остаётся). Замок
                запрещает сдвиг, правку стиля и удаление. Esc снимает выделение. Shift+клик —
                мультивыбор в списке.
              </p>
              <p>Удаление группы не удаляет знаки — они переходят в «Без группы».</p>
            </SectionHelpTooltip>
          </div>
          {sceneId == null ? (
            <p className="text-xs text-muted-foreground">Откройте сцену</p>
          ) : (
            <>
              {canManage && (
                <form
                  className="flex gap-1"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const name = newGroupName.trim();
                    if (!name) return;
                    onCreateLayerGroup(name);
                    setNewGroupName("");
                  }}
                >
                  <Input
                    value={newGroupName}
                    onChange={(event) => setNewGroupName(event.target.value)}
                    placeholder="Новая группа"
                    aria-label="Имя новой группы"
                    className="h-8"
                  />
                  <Button type="submit" size="sm" className="h-8" disabled={!newGroupName.trim()}>
                    +
                  </Button>
                </form>
              )}
              {layerGroups.map((group) => {
                const groupSigns = signsByGroup.get(group.id) ?? [];
                return (
                  <div
                    key={group.id}
                    className={cn(
                      "space-y-0.5 rounded border border-border/60 p-1.5",
                      group.is_visible === false && "opacity-60",
                    )}
                  >
                    <div className="flex items-center gap-1">
                      {group.is_locked ? (
                        <FolderLock className="h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden />
                      ) : null}
                      {group.is_visible === false ? (
                        <EyeOff className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      ) : null}
                      {canManage ? (
                        <Input
                          defaultValue={group.name}
                          className="h-7 text-xs font-medium"
                          aria-label={`Имя группы ${group.name}`}
                          onBlur={(event) => {
                            const next = event.target.value.trim();
                            if (next && next !== group.name) onRenameLayerGroup(group.id, next);
                            else event.target.value = group.name;
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") (event.target as HTMLInputElement).blur();
                          }}
                        />
                      ) : (
                        <span className="min-w-0 flex-1 truncate px-1 text-xs font-medium">
                          {group.name}
                        </span>
                      )}
                      {canManage && (
                        <>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            title={group.is_visible !== false ? "Скрыть группу" : "Показать группу"}
                            aria-label={
                              group.is_visible !== false ? "Скрыть группу" : "Показать группу"
                            }
                            onClick={() =>
                              onToggleGroupVisible(group.id, !(group.is_visible !== false))
                            }
                          >
                            {group.is_visible !== false ? (
                              <Eye className="h-3.5 w-3.5" />
                            ) : (
                              <EyeOff className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            title={group.is_locked ? "Снять блокировку группы" : "Заблокировать группу"}
                            aria-label={
                              group.is_locked ? "Снять блокировку группы" : "Заблокировать группу"
                            }
                            onClick={() => onToggleGroupLock(group.id, !group.is_locked)}
                          >
                            {group.is_locked ? (
                              <Lock className="h-3.5 w-3.5" />
                            ) : (
                              <LockOpen className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            title="Удалить группу (знаки останутся)"
                            aria-label={`Удалить группу ${group.name}`}
                            onClick={() => onDeleteLayerGroup(group.id)}
                          >
                            ×
                          </Button>
                        </>
                      )}
                    </div>
                    <SignRows
                      features={groupSigns}
                      selectedIds={selectedIds}
                      canManage={canManage}
                      layerGroups={layerGroups}
                      onSelectSign={onSelectSign}
                      onDeleteSign={onDeleteSign}
                      onToggleSignLock={onToggleSignLock}
                      onToggleSignVisible={onToggleSignVisible}
                      onAssignSignGroup={onAssignSignGroup}
                    />
                    {groupSigns.length === 0 && (
                      <p className="px-1 text-[11px] text-muted-foreground">Пустая группа</p>
                    )}
                  </div>
                );
              })}
              <div className="space-y-0.5">
                <p className="px-1 text-[11px] font-medium text-muted-foreground">Без группы</p>
                {(signsByGroup.get(null) ?? []).length === 0 && signs.length === 0 ? (
                  <p className="px-1 text-xs text-muted-foreground">Пока пусто</p>
                ) : (
                  <SignRows
                    features={signsByGroup.get(null) ?? []}
                    selectedIds={selectedIds}
                    canManage={canManage}
                    layerGroups={layerGroups}
                    onSelectSign={onSelectSign}
                    onDeleteSign={onDeleteSign}
                    onToggleSignLock={onToggleSignLock}
                    onToggleSignVisible={onToggleSignVisible}
                    onAssignSignGroup={onAssignSignGroup}
                  />
                )}
              </div>
            </>
          )}
        </section>

        <section className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Библиотека</h3>
          <div className="max-h-48 space-y-0.5 overflow-y-auto">
            {(library?.kls ?? []).map((item) => (
              <button
                key={`kls-${item.kls_id}`}
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-muted",
                  pick?.source === "kls" && pick.klsId === item.kls_id && "bg-muted",
                )}
                onClick={() => onPick({ source: "kls", klsId: item.kls_id, name: item.name })}
              >
                {item.icon.startsWith("data:") || item.icon.startsWith("http") ? (
                  <img src={item.icon} alt="" className="h-6 w-6 object-contain" />
                ) : (
                  <span className="inline-block h-6 w-6 rounded bg-muted" />
                )}
                <span className="truncate">{item.name}</span>
              </button>
            ))}
            {(library?.assets ?? []).map((item) => (
              <button
                key={`asset-${item.id}`}
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-muted",
                  pick?.source === "asset" && pick.assetId === item.id && "bg-muted",
                )}
                onClick={() => onPick({ source: "asset", assetId: item.id, name: item.name })}
              >
                {item.url ? <img src={item.url} alt="" className="h-6 w-6 object-contain" /> : null}
                <span className="truncate">{item.name}</span>
              </button>
            ))}
          </div>
          {canManage && (
            <label className="block text-xs text-muted-foreground">
              Загрузить SVG или PNG
              <Input
                type="file"
                accept="image/svg+xml,image/png,.svg,.png"
                className="mt-1"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onUpload(file);
                  event.target.value = "";
                }}
              />
            </label>
          )}
        </section>

        {styleForm}
        {signCardForm}
      </div>
    </aside>
  );
}
